import type { ApiContextValue } from "@tandemdiabetes/sugarmate-api-react-client"
import { ApiContextProvider as SMApiContextProvider } from "@tandemdiabetes/sugarmate-api-react-client"
import { isAxiosError } from "axios"
import type { ReactNode } from "react"
import { useCallback } from "react"
import { useRefreshAccessToken } from "../RefreshTokenHandler/useRefreshAccessToken"
import { useAuthContext } from "src/contexts/AuthContext"
import { getSugarmateError } from "src/utils"

interface Props {
  children?: ReactNode;
  baseURL?: string;
}

export function ApiContextProvider(props: Props) {
  const {
    baseURL,
    children,
  } = props

  const {
    authCookie,
    isExpired,
    isRefreshing,
    authCookieRef,
  } = useAuthContext()

  /*
   * explicitly pass the baseURL here because it won't be inherited
   * from the ApiContextProvider
   */
  const refreshAccessToken = useRefreshAccessToken(baseURL)

  const onRejected = useCallback<
    Exclude<ApiContextValue["onRejected"], undefined | null>
  >(
    (error) => {
      if (isAxiosError(error)) {
        if (process.env.NODE_ENV === "development") {
          const sugarmateError = getSugarmateError(error)
          console.log(
            `error with status ${error.response?.status} for ${error.config?.url}, detail: ${sugarmateError?.detail}`,
          )
        }

        // in the case of 401 unauthorized try to refresh the access token
        if (error.response?.status === 401 && refreshAccessToken) {
          const authHeader = error.config?.headers.Authorization
          const accessToken = authCookieRef.current?.access_token
          const refreshToken = authCookieRef.current?.refresh_token

          /*
           * only refresh the access token if the token in the request is the
           * current access token
           */
          if (
            refreshToken &&
            accessToken &&
            typeof authHeader === "string" &&
            `${authHeader}`.includes(accessToken)
          ) {
            void refreshAccessToken(refreshToken).catch()
          }
        }
      }

      throw error
    },
    [
      refreshAccessToken,
      authCookieRef,
    ],
  )



  return (
    <SMApiContextProvider
      baseURL={baseURL}
      accessToken={isExpired || isRefreshing ? undefined : authCookie?.access_token}
      onRejected={onRejected}
    >
      {children}
    </SMApiContextProvider>
  )
}
